import { Box, Typography, Button, TextField, Menu, MenuItem, Chip } from "@mui/material";
import { useTranslation } from 'react-i18next';
import LoadingGif from "../../components/LoadingGif";
import { useState, useEffect, useMemo } from "react";
import { useRoom } from "../../context/RoomContext";
import Cookies from "js-cookie";
import { ChevronDown, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

const BookingListTab = ({
  handleOpenModal,
  bookings,
  setBookings,
  displayDate,
  setTodayBookings,
  setReviewBookings,
}) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { fetchHotelRooms, ListRoomBookings, loading, } = useRoom();
  const [rooms, setRooms] = useState([]);
  const [roomMenuAnchor, setRoomMenuAnchor] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState("");
  const [selectedRoomName, setSelectedRoomName] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResult, setTotalResult] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null); // null -> "asc" -> "desc" -> null

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const fetchFilteredRooms = async () => {
      const id = Cookies.get("hotelId");
      if (id) {
        const hotelRoom = await fetchHotelRooms(id, "published");
        setRooms(hotelRoom);
      }
    };
    fetchFilteredRooms();
  }, [fetchHotelRooms]);

  useEffect(() => {
    const fetchFilteredBookings = async () => {
      const targetRooms = selectedRoomId
        ? rooms.filter((room) => room._id === selectedRoomId)
        : rooms;
      if (targetRooms.length === 0) {
        setBookings([]);
        return;
      }

      const params = { page };
      if (filter !== "all") {
        params.status = filter;
      } else {
        params.status = "all";
      }
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      const results = await Promise.all( 
        targetRooms.map((room) => ListRoomBookings(room._id, params))
      );
      const allBookings = results.flatMap((result) => result?.data || result || []);
      setBookings(allBookings || []);
      setTodayBookings(allBookings || []);
      setReviewBookings(allBookings || []);
      const maxPageArray = results.flatMap((result) => result.pagination.totalPages || []);
      const maxPage = Math.max(...maxPageArray, 1);
      setTotalPages(maxPage);
      setTotalResult(allBookings?.length || 0);
    };
    fetchFilteredBookings();
  }, [ListRoomBookings, rooms, selectedRoomId, filter, debouncedSearch, page]);

  const decideStatus = (booking) => {
    const today = new Date().toISOString().split('T')[0];

    if (!booking.checkIn.isCheckedIn && new Date(booking.checkIn.scheduledCheckIn).toISOString().split('T')[0] === today && !booking.checkOut.isCheckOut) {
      return "CheckIn";
    } else if (booking.checkIn.isCheckedIn && new Date(booking.checkOut.scheduledCheckOut).toISOString().split('T')[0] === today && !booking.checkOut.isCheckOut) {
      return "CheckOut";
    } else if (booking.review && booking.review.bookingCancelled && !booking.checkIn.isCheckedIn && !booking.checkOut.isCheckOut) {
      return "Cancelled";
    } else if (booking.review && !booking.review.bookingConfirmed && booking.review.unconfirmReason === 'RequiredInfoDocs1'
      && !booking.documentsUploaded && !booking.checkIn.isCheckedIn && !booking.checkOut.isCheckOut) {
      return "pendingInfoNeeded";
    } else if (booking.review && !booking.review.bookingConfirmed && booking.review.unconfirmReason === 'RequiredInfoDocs1'
      && booking.documentsUploaded && !booking.checkIn.isCheckedIn && !booking.checkOut.isCheckOut) {
      return "pendingInfoUpdated";
    } else if ((!booking.review || !booking.review.bookingConfirmed) && !booking.checkIn.isCheckedIn && !booking.checkOut.isCheckOut) {
      return "Pending";
    } else if (booking.review && booking.review.bookingConfirmed && !booking.checkIn.isCheckedIn && !booking.checkOut.isCheckOut) {
      return "Confirmed";
    } else if (booking.checkIn.isCheckedIn && !booking.checkOut.isCheckOut) {
      return "inStay";
    } else if (booking.checkIn.isCheckedIn && booking.checkOut.isCheckOut) {
      return "Finished";
    }
  };

  const headerCellStyle = {
    fontWeight: "700",
    color: "#000",
    textAlign: "center",
    backgroundColor: "#DDD",
    padding: "10px",
    mb: 2,
  };

  const tableCellStyle = {
    color: "#666",
    textAlign: "center",
    py: 1,
  };

  const handleSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else {
      setSortField(null);
      setSortDirection(null);
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown size={14} />;
    return sortDirection === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const sortedBookings = useMemo(() => {
    if (!sortField || !sortDirection) return bookings;

    const getSortValue = (booking) => {
      if (sortField === "bookingNo") return booking.bookingNo;
      if (sortField === "checkIn") return new Date(booking.checkIn.scheduledCheckIn).getTime();
      if (sortField === "checkOut") return new Date(booking.checkOut.scheduledCheckOut).getTime();
      return null;
    };

    return [...bookings].sort((a, b) => {
      const aVal = getSortValue(a);
      const bVal = getSortValue(b);
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [bookings, sortField, sortDirection]);

  const filterButtonStyle = {
    borderRadius: "20px",
    padding: "8px 16px",
    textTransform: "none",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    border: "1px solid #E5E7EB",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#31bbcd7f",
    },
  };

  return (
    loading ? (
      <LoadingGif />
    ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, }}>
        <Box sx={{ width: "100%", overflowX: "auto", }}>
          {/* Filter bar */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 2, minWidth: "1000px",}}>
            <Button
              onClick={() => { setFilter("all"); setPage(1); }}
              sx={{
                ...filterButtonStyle,
                backgroundColor: filter === "all" ? "#31BACD" : "#fff",
                color: filter === "all" ? "#fff" : "#000",
              }}
            >
              <div>{t('AllBookings')}</div>
              {filter === "all" && (
                <Box sx={{ width: "25px", height: "25px", borderRadius: "50%", backgroundColor: "#FFFFFF40", fontSize: "14px", }}>
                  {totalResult}
                </Box>
              )}
            </Button>
            <Button
              onClick={() => { setFilter("pending"); setPage(1); }}
              sx={{
                ...filterButtonStyle,
                backgroundColor: filter === "pending" ? "#31BACD" : "#fff",
                color: filter === "pending" ? "#fff" : "#000",
              }}
            >
              <div>{t('Pending')}</div>
              {filter === "pending" && (
                <Box sx={{ width: "25px", height: "25px", borderRadius: "50%", backgroundColor: "#FFFFFF40", fontSize: "14px", }}>
                  {totalResult}
                </Box>
              )}
            </Button>
            <Button
              onClick={() => { setFilter("confirmed"); setPage(1); }}
              sx={{
                ...filterButtonStyle,
                backgroundColor: filter === "confirmed" ? "#31BACD" : "#fff",
                color: filter === "confirmed" ? "#fff" : "#000",
              }}
            >
              <div>{t('Confirmed')}</div>
              {filter === "confirmed" && (
                <Box sx={{ width: "25px", height: "25px", borderRadius: "50%", backgroundColor: "#FFFFFF40", fontSize: "14px", }}>
                  {totalResult}
                </Box>
              )}
            </Button>
            <Button
              onClick={() => { setFilter("checkIn"); setPage(1); }}
              sx={{
                ...filterButtonStyle,
                backgroundColor: filter === "checkIn" ? "#31BACD" : "#fff",
                color: filter === "checkIn" ? "#fff" : "#000",
              }}
            >
              <div>{t('CheckIn')}</div>
              {filter === "checkIn" && (
                <Box sx={{ width: "25px", height: "25px", borderRadius: "50%", backgroundColor: "#FFFFFF40", fontSize: "14px", }}>
                  {totalResult}
                </Box>
              )}
            </Button>
            <Button
              onClick={() => { setFilter("checkOut"); setPage(1); }}
              sx={{
                ...filterButtonStyle,
                backgroundColor: filter === "checkOut" ? "#31BACD" : "#fff",
                color: filter === "checkOut" ? "#fff" : "#000",
              }}
            >
              <div>{t('CheckOut')}</div>
              {filter === "checkOut" && (
                <Box sx={{ width: "25px", height: "25px", borderRadius: "50%", backgroundColor: "#FFFFFF40", fontSize: "14px", }}>
                  {totalResult}
                </Box>
              )}
            </Button>
            <Button
              onClick={() => { setFilter("inStay"); setPage(1); }}
              sx={{
                ...filterButtonStyle,
                backgroundColor: filter === "inStay" ? "#31BACD" : "#fff",
                color: filter === "inStay" ? "#fff" : "#000",
              }}
            >
              <div>{t('inStay')}</div>
              {filter === "inStay" && (
                <Box sx={{ width: "25px", height: "25px", borderRadius: "50%", backgroundColor: "#FFFFFF40", fontSize: "14px", }}>
                  {totalResult}
                </Box>
              )}
            </Button>
            <Button
              onClick={() => { setFilter("finished"); setPage(1); }}
              sx={{
                ...filterButtonStyle,
                backgroundColor: filter === "finished" ? "#31BACD" : "#fff",
                color: filter === "finished" ? "#fff" : "#000",
              }}
            >
              <div>{t('Finished')}</div>
              {filter === "finished" && (
                <Box sx={{ width: "25px", height: "25px", borderRadius: "50%", backgroundColor: "#FFFFFF40", fontSize: "14px", }}>
                  {totalResult}
                </Box>
              )}
            </Button>
            <Button
              onClick={() => { setFilter("cancelled"); setPage(1); }}
              sx={{
                ...filterButtonStyle,
                backgroundColor: filter === "cancelled" ? "#31BACD" : "#fff",
                color: filter === "cancelled" ? "#fff" : "#000",
              }}
            >
              <div>{t('Cancelled')}</div>
              {filter === "cancelled" && (
                <Box sx={{ width: "25px", height: "25px", borderRadius: "50%", backgroundColor: "#FFFFFF40", fontSize: "14px", }}>
                  {totalResult}
                </Box>
              )}
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 2, }}>
          <TextField
            fullWidth
            placeholder={t("SearchBy")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "10px",
              },
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, }}>
            <Button
              onClick={(e) => setRoomMenuAnchor(e.currentTarget)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                padding: "8px 16px",
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                borderRadius: "10px",
                fontSize: "16px",
                textTransform: "none",
                color: "#000",
                whiteSpace: "nowrap",
              }}
            >
              {selectedRoomName || t("RoomType")}
              <ChevronDown size={20} />
            </Button>
            <Menu
              anchorEl={roomMenuAnchor}
              open={Boolean(roomMenuAnchor)}
              onClose={() => setRoomMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setSelectedRoomId("");
                  setSelectedRoomName("");
                  setPage(1);
                  setRoomMenuAnchor(null);
                }}
              >
                {t("All")}
              </MenuItem>
              {rooms.map((room) => (
                <MenuItem
                  key={room._id}
                  onClick={() => {
                    setSelectedRoomId(room._id);
                    setSelectedRoomName(room.information1.roomName);
                    setPage(1);
                    setRoomMenuAnchor(null);
                  }}
                >
                  {room.information1.roomName}
                </MenuItem>
              ))}
            </Menu>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#FFFFFF", borderRadius: "10px", gap: 1, }}>
              <Button
                onClick={() => {
                  setPage((prev) => Math.max(1, prev - 1));
                }}
                disabled={page <= 1}
                sx={{ minWidth: "auto", color: "#31BACD", padding: "8px", borderRadius: "10px", "&:disabled": { color: "#D1D1D1" } }}
              >
                <ChevronLeft size={20} />
              </Button>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", }}>
                <Typography sx={{ fontSize: "16px", color: "#000", textAlign: "center", padding: "6px", }}>
                  {t("Page")}:
                </Typography>
                <Typography sx={{ fontSize: "16px", color: "#000", textAlign: "center", }}>
                  {page}
                </Typography>
                <Typography sx={{ fontSize: "16px", color: "#000", textAlign: "center", padding: "6px", }}>
                  /
                </Typography>
                <Typography sx={{ fontSize: "16px", color: "#000", textAlign: "center", }}>
                  {totalPages}
                </Typography>
              </Box>
              <Button
                onClick={() => {
                  setPage((prev) => Math.min(totalPages, prev + 1));
                }}
                disabled={page >= totalPages}
                sx={{ minWidth: "auto", color: "#31BACD", padding: "8px", borderRadius: "10px", "&:disabled": { color: "#D1D1D1" } }}
              >
                <ChevronRight size={20} />
              </Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ overflowX: "auto", backgroundColor: "#FFF", padding: "0 0 10px 0", borderRadius: "10px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", }}>
          <Box
            sx={{
              minWidth: "1100px",
              display: "grid",
              alignItems: "center",
              gridTemplateColumns: "repeat(8, 1fr)",
              fontSize: "14px",
            }}
          >
            {/* Header */}
            <Typography sx={{ ...headerCellStyle, borderRadius: "10px 0 0 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
              {t("BookingNo")}
              <Box component="span" onClick={() => handleSort("bookingNo")} sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                {renderSortIcon("bookingNo")}
              </Box>
            </Typography>
            <Typography sx={headerCellStyle}>{t("Owner")}</Typography>
            <Typography sx={headerCellStyle}>{t("PetName")}</Typography>
            <Typography sx={headerCellStyle}>{t("Contact")}</Typography>
            <Typography sx={{ ...headerCellStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
              {t("CheckIn")}
              <Box component="span" onClick={() => handleSort("checkIn")} sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                {renderSortIcon("checkIn")}
              </Box>
            </Typography>
            <Typography sx={{ ...headerCellStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
              {t("CheckOut")}
              <Box component="span" onClick={() => handleSort("checkOut")} sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                {renderSortIcon("checkOut")}
              </Box>
            </Typography>
            <Typography sx={headerCellStyle}>{t("Status")}</Typography>
            <Typography sx={{ ...headerCellStyle, borderRadius: "0 10px 0 0", }}>{t("Action")}</Typography>

            {/* Data Rows */}
            {sortedBookings.length > 0 && (
              sortedBookings.map((event) => (
                <>
                  <Typography sx={{ ...tableCellStyle, backgroundColor: decideStatus(event) === "Cancelled" && "#FF00001A" }}>#{event.bookingNo}</Typography>
                  <Typography sx={{ ...tableCellStyle, backgroundColor: decideStatus(event) === "Cancelled" && "#FF00001A" }}>{event.name}</Typography>
                  <Typography sx={{ ...tableCellStyle, backgroundColor: decideStatus(event) === "Cancelled" && "#FF00001A" }}>{event.petName}</Typography>
                  <Typography sx={{ ...tableCellStyle, backgroundColor: decideStatus(event) === "Cancelled" && "#FF00001A" }}>{event.phoneNumber}</Typography>
                  <Typography sx={{ ...tableCellStyle, backgroundColor: decideStatus(event) === "Cancelled" && "#FF00001A" }}>{displayDate(event.checkIn.scheduledCheckIn)}</Typography>
                  <Typography sx={{ ...tableCellStyle, backgroundColor: decideStatus(event) === "Cancelled" && "#FF00001A" }}>{displayDate(event.checkOut.scheduledCheckOut)}</Typography>
                  <Box sx={{ ...tableCellStyle, backgroundColor: decideStatus(event) === "Cancelled" && "#FF00001A", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Chip
                      label={t(decideStatus(event))}
                      size="large"
                      sx={{
                        backgroundColor: decideStatus(event) === "CheckOut" ? "#E5194A" : decideStatus(event) === "CheckIn" ? "#FFB60D" :
                          decideStatus(event) === "inStay" ? "#FF6900" : decideStatus(event) === "Confirmed" ? "#00A63E" :
                            decideStatus(event) === "Finished" ? "#E5E7EB" : decideStatus(event) === "pendingInfoNeeded" ||
                              decideStatus(event) === "Cancelled" ? "#FFF" : "#31BACD",
                        color: decideStatus(event) === "pendingInfoNeeded" ? "#666" : decideStatus(event) === "Cancelled" ? "#E5194A" : "#fff",
                        fontSize: "12px",
                        border: decideStatus(event) === "pendingInfoNeeded" ? "1px solid #666" : decideStatus(event) === "Cancelled" ? "1px solid #E5194A" : "none",
                        height: "25px",
                        width: "150px",
                      }}
                    />
                  </Box>
                  <Box sx={{ py: 0.5, backgroundColor: decideStatus(event) === "Cancelled" && "#FF00001A", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Button
                      sx={{
                        fontSize: "14px",
                        padding: "4px 8px",
                        margin: "0 auto",
                        backgroundColor: "#fff",
                        color: "#31BACD",
                        border: "1px solid #31BACD",
                        textTransform: "none",
                        borderRadius: "4px",
                        width: "90px",
                        "&:hover": { backgroundColor: "#BBDEFB" },
                      }}
                      onClick={() => handleOpenModal(event._id, 
                        decideStatus(event) === "pendingInfoNeeded" ? "editReview" : 
                        decideStatus(event) === "CheckIn" ? "checkIn" : 
                        decideStatus(event) === "inStay" ? "askInStay" :
                        decideStatus(event) === "Confirmed" ? "askConfirm" : 
                        decideStatus(event) === "Finished" ? "showFinished" : 
                        decideStatus(event) === "Cancelled" ? "showCancelled" :
                        decideStatus(event) === "CheckOut" ? "editCheckOut" : "review")}
                    > 
                      {decideStatus(event) === "Cancelled" || decideStatus(event) === "Finished" ? t("View") : t("Manage")}
                    </Button>
                  </Box>
                </>
              ))
            )}
          </Box>
          {sortedBookings.length === 0 && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, my: 5, }}>
              <img src="/icons/noRooms.svg" alt="paw" style={{ width: "16px", height: "auto" }} />
              <Box sx={{ fontSize: "16px", color: "#D9D9D9", fontWeight: 700, }}>
                {t('NoBookingsFound')}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    )
  );
};

export default BookingListTab;
